import React, {useRef} from 'react';
import {
    Button,
    Form,
    Input,
    Alert,
    Typography,
    Drawer,
    FloatButton,
} from 'antd';
import '@ant-design/v5-patch-for-react-19';
import {
    PlusOutlined,
    EditOutlined,
    DeleteOutlined,
    LockOutlined,
    LogoutOutlined,
    ExportOutlined,
    ImportOutlined,
    SafetyOutlined,
    CopyOutlined,
    CheckOutlined,
    UndoOutlined,
    KeyOutlined,
    SearchOutlined,
    SettingOutlined,
    SafetyCertificateOutlined,
    LinkOutlined,
} from '@ant-design/icons';
import {SyncSettings} from '../SyncSettings';
import UpdateNotifier from '../UpdateNotifier';
import selfPassLogo from '../assets/selfpass-logo.png';
import usePasswordManager, {appVersion} from '../hooks/usePasswordManager';
import './MobileApp.css';

const {Paragraph, Text} = Typography;

const MobileApp = () => {
    const importInputRef = useRef(null);
    const pm = usePasswordManager();

    const {
        form,
        authForm,
        isModalVisible,
        setIsModalVisible,
        editingPassword,
        authVisible,
        vaultSession,
        copiedPasswordId,
        copiedLoginId,
        welcomeVisible,
        saltChoiceVisible,
        newSalt,
        setNewSalt,
        customSalt,
        setCustomSalt,
        step,
        setStep,
        searchTerm,
        setSearchTerm,
        isNewVault,
        isUnlocking,
        syncSettingsKey,
        isYandexSyncing,
        setIsYandexSyncing,
        activePage,
        setActivePage,
        lockVault,
        confirmExitVault,
        handleWelcomeContinue,
        handleSaltChoice,
        handleCustomSaltSubmit,
        handleNewSaltConfirm,
        generatePassword,
        copyToClipboard,
        handleAuth,
        handleExportEncrypted,
        handleExportForExtension,
        handleImport,
        handleAdd,
        handleEdit,
        handleDelete,
        handleRestore,
        handleSubmit,
        filteredPasswords,
        setPasswords,
    } = pm;

    const isLocked = authVisible || !vaultSession;
    const showMainApp = !welcomeVisible && !saltChoiceVisible && !authVisible;

    const renderWelcome = () => (
        <div className="mobile-gate">
            <div className="mobile-gate__brand">
                <img src={selfPassLogo} alt="SelfPass" className="mobile-gate__logo" />
                <p className="mobile-gate__subtitle">Локальный менеджер паролей</p>
            </div>

            <div className="mobile-gate__body">
                <p className="mobile-gate__description">
                    SelfPass хранит пароли в зашифрованном сейфе на вашем устройстве.
                    Доступ защищён ключом шифрования и мастер-паролем — без них данные
                    восстановить нельзя.
                </p>
                <div className="mobile-stack">
                    <Button
                        type="primary"
                        size="large"
                        block
                        onClick={handleWelcomeContinue}
                    >
                        Начать настройку
                    </Button>
                </div>
            </div>
        </div>
    );

    const renderSaltSetup = () => (
        <div className="mobile-gate">
            <div className="mobile-gate__brand">
                <img src={selfPassLogo} alt="SelfPass" className="mobile-gate__logo" />
                <h1 className="mobile-gate__title">Настройка безопасности</h1>
            </div>

            {step === 1 && (
                <div className="mobile-gate__body">
                    <Alert
                        message="Важное предупреждение"
                        description="Ключ — критический элемент безопасности. Потеря ключа сделает восстановление данных невозможным."
                        type="warning"
                        showIcon
                        style={{marginBottom: 20}}
                    />
                    <div className="mobile-stack">
                        <Button
                            type="primary"
                            size="large"
                            block
                            onClick={() => handleSaltChoice('generate')}
                        >
                            Сгенерировать новый ключ
                        </Button>
                        <Button
                            size="large"
                            block
                            onClick={() => handleSaltChoice('custom')}
                        >
                            Использовать свой ключ
                        </Button>
                    </div>
                </div>
            )}

            {step === 2 && (
                <div className="mobile-gate__body">
                    <Alert
                        message="Сохраните ключ"
                        description="Храните эту строку в надёжном месте. Без неё доступ восстановить нельзя."
                        type="error"
                        showIcon
                    />
                    <div className="mobile-salt-box">
                        <Text code className="mobile-salt-box__value">{newSalt}</Text>
                        <Paragraph
                            copyable={{text: newSalt, icon: [<CopyOutlined key="copy"/>]}}
                            className="mobile-salt-box__copy"
                        />
                    </div>
                    <div className="mobile-stack">
                        <Button type="primary" size="large" block onClick={handleNewSaltConfirm}>
                            Я сохранил ключ, продолжить
                        </Button>
                        <Button
                            size="large"
                            block
                            onClick={() => {
                                setNewSalt('');
                                setStep(1);
                            }}
                        >
                            Отмена
                        </Button>
                    </div>
                </div>
            )}

            {step === 3 && (
                <div className="mobile-gate__body">
                    <Form layout="vertical" onFinish={handleCustomSaltSubmit}>
                        <Form.Item
                            label="Введите ваш ключ"
                            required
                        >
                            <Input.Password
                                size="large"
                                value={customSalt}
                                onChange={(e) => setCustomSalt(e.target.value)}
                                placeholder="Минимум 16 символов"
                            />
                        </Form.Item>
                        <div className="mobile-stack">
                            <Button type="primary" size="large" block htmlType="submit">
                                Подтвердить ключ
                            </Button>
                            <Button size="large" block onClick={() => setStep(1)}>
                                Назад
                            </Button>
                        </div>
                    </Form>
                </div>
            )}
        </div>
    );

    const renderUnlock = () => (
        <div className="mobile-gate">
            <div className="mobile-gate__brand">
                <img src={selfPassLogo} alt="SelfPass" className="mobile-gate__logo" />
                <p className="mobile-gate__subtitle">
                    {isNewVault ? 'Создайте мастер-пароль' : 'Введите мастер-пароль'}
                </p>
            </div>

            <div className="mobile-gate__body">
                {isNewVault && (
                    <Alert
                        message="Новый сейф"
                        description="Мастер-пароль не хранится на сервере и не может быть восстановлен."
                        type="info"
                        showIcon
                        style={{marginBottom: 16}}
                    />
                )}
                <Form form={authForm} layout="vertical" onFinish={handleAuth}>
                    <Form.Item
                        name="password"
                        rules={[
                            {required: true, message: 'Введите мастер-пароль'},
                            ...(isNewVault ? [{min: 8, message: 'Минимум 8 символов'}] : []),
                        ]}
                    >
                        <Input.Password
                            size="large"
                            prefix={<SafetyOutlined/>}
                            placeholder="Мастер-пароль"
                            autoComplete="current-password"
                        />
                    </Form.Item>
                    {isNewVault && (
                        <Form.Item
                            name="confirmPassword"
                            dependencies={['password']}
                            rules={[
                                {required: true, message: 'Подтвердите мастер-пароль'},
                                ({getFieldValue}) => ({
                                    validator(_, value) {
                                        if (!value || getFieldValue('password') === value) {
                                            return Promise.resolve();
                                        }
                                        return Promise.reject(new Error('Пароли не совпадают'));
                                    },
                                }),
                            ]}
                        >
                            <Input.Password
                                size="large"
                                prefix={<SafetyOutlined/>}
                                placeholder="Повторите мастер-пароль"
                                autoComplete="new-password"
                            />
                        </Form.Item>
                    )}
                    <div className="mobile-stack">
                        <Button type="primary" size="large" block htmlType="submit" loading={isUnlocking}>
                            {isNewVault ? 'Создать сейф' : 'Войти'}
                        </Button>
                        {!isNewVault && (
                            <Button
                                danger
                                size="large"
                                block
                                icon={<LogoutOutlined/>}
                                onClick={confirmExitVault}
                                disabled={isUnlocking}
                            >
                                Другое хранилище
                            </Button>
                        )}
                    </div>
                </Form>
            </div>

            <div className="mobile-gate__footer">
                <span>v{appVersion}</span>
                <UpdateNotifier />
            </div>
        </div>
    );

    const renderPasswordList = () => (
        <>
            <header className="mobile-topbar">
                <div className="mobile-topbar__row">
                    <img src={selfPassLogo} alt="SelfPass" className="mobile-topbar__logo" />
                    <Button
                        type="text"
                        icon={<LockOutlined/>}
                        onClick={lockVault}
                        aria-label="Заблокировать"
                        className="mobile-icon-btn"
                    />
                </div>
                <Input
                    placeholder="Поиск..."
                    prefix={<SearchOutlined/>}
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    size="large"
                    allowClear
                    className="mobile-search"
                />
                <SyncSettings
                    key={syncSettingsKey}
                    variant="status"
                    vaultSession={vaultSession}
                    onSyncingChange={setIsYandexSyncing}
                    onSync={setPasswords}
                />
            </header>

            <main className="mobile-content">
                {filteredPasswords.length === 0 ? (
                    <div className="mobile-empty">
                        <SafetyCertificateOutlined className="mobile-empty__icon" />
                        <p>{searchTerm ? 'Ничего не найдено' : 'Пока нет сохранённых паролей'}</p>
                        {!searchTerm && (
                            <Button type="primary" icon={<PlusOutlined/>} onClick={handleAdd}>
                                Добавить запись
                            </Button>
                        )}
                    </div>
                ) : (
                    <ul className="mobile-list">
                        {filteredPasswords.map((record) => (
                            <li
                                key={record.key}
                                className={`mobile-list-item${record.deletedAt ? ' mobile-list-item--deleted' : ''}`}
                            >
                                <button
                                    type="button"
                                    className="mobile-list-item__main"
                                    onClick={() => {
                                        if (!record.deletedAt) {
                                            handleEdit(record);
                                        }
                                    }}
                                >
                                    <span className="mobile-list-item__name">{record.name}</span>
                                    <span className="mobile-list-item__meta">
                                        {record.username || 'Без логина'}
                                    </span>
                                </button>

                                <div className="mobile-list-item__actions">
                                    {record.deletedAt ? (
                                        <Button
                                            type="text"
                                            icon={<UndoOutlined/>}
                                            onClick={() => handleRestore(record.key)}
                                            className="mobile-icon-btn"
                                            style={{color: '#52c41a'}}
                                            aria-label="Восстановить"
                                        />
                                    ) : (
                                        <>
                                            {record.username ? (
                                                <Button
                                                    type="text"
                                                    icon={copiedLoginId === record.key ? <CheckOutlined/> : <CopyOutlined/>}
                                                    onClick={() => copyToClipboard(record.username, record.key, 'login')}
                                                    className="mobile-icon-btn"
                                                    aria-label="Копировать логин"
                                                />
                                            ) : null}
                                            <Button
                                                type="text"
                                                icon={copiedPasswordId === record.key ? <CheckOutlined/> : <CopyOutlined/>}
                                                onClick={() => copyToClipboard(record.password, record.key, 'password')}
                                                className="mobile-icon-btn mobile-icon-btn--primary"
                                                aria-label="Копировать пароль"
                                            />
                                            <Button
                                                type="text"
                                                icon={<EditOutlined/>}
                                                onClick={() => handleEdit(record)}
                                                className="mobile-icon-btn"
                                                aria-label="Редактировать"
                                            />
                                        </>
                                    )}
                                </div>
                            </li>
                        ))}
                    </ul>
                )}
            </main>

            <FloatButton
                type="primary"
                icon={<PlusOutlined/>}
                onClick={handleAdd}
                disabled={isLocked || isYandexSyncing}
                className="mobile-fab"
                aria-label="Добавить пароль"
            />
        </>
    );

    const renderSettings = () => (
        <>
            <header className="mobile-topbar">
                <div className="mobile-topbar__row">
                    <h1 className="mobile-topbar__heading">Настройки</h1>
                    <Button
                        type="text"
                        icon={<LockOutlined/>}
                        onClick={lockVault}
                        aria-label="Заблокировать"
                        className="mobile-icon-btn"
                    />
                </div>
            </header>

            <main className="mobile-content mobile-content--settings">
                <section className="mobile-section">
                    <h2 className="mobile-section__title">Синхронизация</h2>
                    <SyncSettings
                        key={syncSettingsKey}
                        variant="full"
                        vaultSession={vaultSession}
                        onSyncingChange={setIsYandexSyncing}
                        onSync={setPasswords}
                    />
                </section>

                <section className="mobile-section">
                    <h2 className="mobile-section__title">Импорт и экспорт</h2>
                    <div className="mobile-stack">
                        <Button
                            size="large"
                            block
                            icon={<ExportOutlined/>}
                            onClick={handleExportEncrypted}
                            disabled={isLocked}
                        >
                            Экспорт (зашифрованный)
                        </Button>
                        <Button
                            size="large"
                            block
                            icon={<KeyOutlined/>}
                            onClick={handleExportForExtension}
                            disabled={isLocked}
                        >
                            Экспорт для расширения
                        </Button>
                        <Button
                            size="large"
                            block
                            icon={<ImportOutlined/>}
                            onClick={() => importInputRef.current?.click()}
                            disabled={isLocked}
                        >
                            Импорт
                        </Button>
                        <input
                            ref={importInputRef}
                            type="file"
                            accept=".json"
                            style={{display: 'none'}}
                            onChange={(e) => {
                                if (e.target.files[0]) {
                                    handleImport(e.target.files[0]);
                                }
                                e.target.value = null;
                            }}
                        />
                    </div>
                </section>

                <section className="mobile-section">
                    <h2 className="mobile-section__title">Хранилище</h2>
                    <div className="mobile-stack">
                        <Button
                            size="large"
                            block
                            icon={<LockOutlined/>}
                            onClick={lockVault}
                            disabled={isLocked}
                        >
                            Заблокировать
                        </Button>
                        <Button
                            size="large"
                            block
                            danger
                            icon={<LogoutOutlined/>}
                            onClick={confirmExitVault}
                            disabled={(authVisible && isNewVault) || isYandexSyncing}
                        >
                            Выйти из хранилища
                        </Button>
                    </div>
                </section>

                <div className="mobile-version">
                    <span>Версия {appVersion}</span>
                    <UpdateNotifier />
                </div>
            </main>
        </>
    );

    const renderEntryDrawer = () => (
        <Drawer
            title={editingPassword ? 'Редактировать' : 'Новая запись'}
            placement="bottom"
            height="90%"
            open={isModalVisible}
            onClose={() => setIsModalVisible(false)}
            className="mobile-drawer"
            destroyOnClose={false}
            extra={
                editingPassword && !editingPassword.deletedAt ? (
                    <Button
                        type="text"
                        danger
                        icon={<DeleteOutlined/>}
                        onClick={() => {
                            setIsModalVisible(false);
                            handleDelete(editingPassword.key);
                        }}
                        aria-label="Удалить"
                    />
                ) : null
            }
            footer={
                <div className="mobile-drawer__footer">
                    <Button size="large" block onClick={() => setIsModalVisible(false)}>
                        Отмена
                    </Button>
                    <Button type="primary" size="large" block onClick={handleSubmit}>
                        {editingPassword ? 'Сохранить' : 'Добавить'}
                    </Button>
                </div>
            }
        >
            <Form form={form} layout="vertical" className="mobile-entry-form">
                <Form.Item
                    name="name"
                    label="Название"
                    rules={[{required: true, message: 'Введите название'}]}
                >
                    <Input size="large"/>
                </Form.Item>

                <Form.Item
                    name="username"
                    label="Логин"
                    rules={[{required: true, message: 'Введите логин'}]}
                >
                    <Input size="large" autoComplete="username"/>
                </Form.Item>

                <Form.Item
                    name="password"
                    label="Пароль"
                    rules={[{required: true, message: 'Введите пароль'}]}
                >
                    <Input.Password
                        size="large"
                        autoComplete="new-password"
                        addonAfter={
                            <Button
                                type="text"
                                onClick={generatePassword}
                                icon={<KeyOutlined/>}
                                aria-label="Сгенерировать пароль"
                            />
                        }
                    />
                </Form.Item>

                <Form.Item
                    name="url"
                    label="URL"
                    rules={[{type: 'url', message: 'Введите корректный URL'}]}
                >
                    <Input size="large" prefix={<LinkOutlined/>} placeholder="https://"/>
                </Form.Item>

                {editingPassword && (
                    <div className="mobile-entry-quick">
                        <Button
                            size="large"
                            block
                            icon={<CopyOutlined/>}
                            onClick={() => copyToClipboard(
                                form.getFieldValue('username') || editingPassword.username,
                                editingPassword.key,
                                'login'
                            )}
                        >
                            Копировать логин
                        </Button>
                        <Button
                            size="large"
                            block
                            type="primary"
                            icon={<CopyOutlined/>}
                            onClick={() => copyToClipboard(
                                form.getFieldValue('password') || editingPassword.password,
                                editingPassword.key,
                                'password'
                            )}
                        >
                            Копировать пароль
                        </Button>
                    </div>
                )}
            </Form>
        </Drawer>
    );

    if (welcomeVisible) {
        return <div className="mobile-app">{renderWelcome()}</div>;
    }

    if (saltChoiceVisible) {
        return <div className="mobile-app">{renderSaltSetup()}</div>;
    }

    if (authVisible) {
        return <div className="mobile-app">{renderUnlock()}</div>;
    }

    return (
        <div className="mobile-app">
            {activePage === 'settings' ? renderSettings() : renderPasswordList()}

            <nav className="mobile-tabbar" aria-label="Основная навигация">
                <button
                    type="button"
                    className={`mobile-tab${activePage === 'vault' ? ' mobile-tab--active' : ''}`}
                    onClick={() => setActivePage('vault')}
                >
                    <SafetyCertificateOutlined/>
                    <span>Сейф</span>
                </button>
                <button
                    type="button"
                    className={`mobile-tab${activePage === 'settings' ? ' mobile-tab--active' : ''}`}
                    onClick={() => setActivePage('settings')}
                    disabled={isLocked}
                >
                    <SettingOutlined/>
                    <span>Настройки</span>
                </button>
            </nav>

            {showMainApp && renderEntryDrawer()}
        </div>
    );
};

export default MobileApp;
